import React, { useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

// Funciones auxiliares para ordenar fechas y campos anidados correctamente dentro del DataGrid
const parseDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
  }
  return 0;
};

const getPropertyByColumn = (obj, path) => {
  if (path === "assigned") return obj.assigned?.full_name || "S/A";
  if (path === "status") return obj.status?.data || "";
  return obj[path] || "";
};

const DataGrid = ({ 
  columns, 
  rows, 
  pageSize = 10, 
  handleEdit, 
  hasFilter, 
  noDataMessage, 
  backgroundColor, 
  setTicketSelected, 
  setShowAssignTicket, 
  setShowMessageConfirmReAssign, 
  setShowMessageConfirmReOpen, 
  account
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Estados de ordenamiento locales en el DataGrid
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Ordenamiento que agrupa textos, fechas y campos anidados
  const sortedRows = [...rows].sort((a, b) => {
    if (!sortField) return 0;
    let valueA = getPropertyByColumn(a, sortField);
    let valueB = getPropertyByColumn(b, sortField);

    if (sortField === "created_at") {
      valueA = parseDate(valueA);
      valueB = parseDate(valueB);
    } else if (typeof valueA === "string" && typeof valueB === "string") {
      valueA = valueA.trim().toLowerCase();
      valueB = valueB.trim().toLowerCase();
      const comparison = valueA.localeCompare(valueB, 'es', { sensitivity: 'base' });
      return sortDirection === "asc" ? comparison : -comparison;
    }

    if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
    if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const totalPages = Math.ceil(rows.length / pageSize);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAssignTicket = (ticket) => {
    setTicketSelected(ticket.id);
    setShowAssignTicket(true);
  };

  const handleReAssignTicket = (ticket) => {
    setTicketSelected(ticket.id);
    setShowMessageConfirmReAssign(true);
  };

  const handleReOpenTicket = (ticket) => {
    setTicketSelected(ticket.id);
    setShowMessageConfirmReOpen(true);
  };

  return (
    <Box className="data-grid" sx={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", backgroundColor: backgroundColor ? backgroundColor : "#D9D9D9", border: "unset" }}>
        <thead>
          <tr style={{ width: "100%", backgroundColor: "#F3F3F3" }}>
            {columns.map((column, index) => (
              <th
                key={column.field}
                onClick={() => handleSort(column.field)}
                style={{
                  textAlign: column.headerAlign || "left",
                  height: column.height || "50px",
                  padding: "10px",
                  cursor: "pointer",
                  color: "#868FA0",
                  borderTopLeftRadius: index === 0 ? "20px" : ""
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {column.headerName}
                  </Box>
                  <Box sx={{ justifyContent: "right" }}>
                    <Box
                      sx={{
                        marginLeft: "2px",
                        fontSize: "14px",
                        color: sortField === column.field && sortDirection === "asc" ? "#000" : "#0000008A",
                      }}
                    >
                      <ArrowDropUpIcon sx={{ marginBottom: "-10px" }} />
                    </Box>
                    <Box
                      sx={{
                        marginLeft: "2px",
                        fontSize: "14px",
                        color: sortField === column.field && sortDirection === "desc" ? "#000" : "#0000008A",
                      }}
                    >
                      <ArrowDropDownIcon sx={{ marginTop: "-10px" }} />
                    </Box>
                  </Box>
                </Box>
              </th>
            ))}
            <th style={{ borderTopRightRadius: "20px" }}></th>
          </tr>
        </thead>
        <tbody>
          {paginatedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} style={{ height: "600px", textAlign: "center", padding: "20px" }}>
                <Typography variant="body1" color="textSecondary">
                  {noDataMessage}
                </Typography>
              </td>
            </tr>
          ) : (
            [
              ...paginatedRows,
              ...Array(pageSize - paginatedRows.length).fill({}),
            ].map((row, index) => (
              <React.Fragment key={index}>
                <tr
                  className={selectedRow === row ? "selected" : ""}
                  onClick={() => Object.keys(row).length !== 0 && handleRowClick(row)}
                  style={{
                    borderLeft: "1px solid #F3F3F3",
                    borderRight: "1px solid #F3F3F3"
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={column.field}
                      style={{
                        textAlign: column.align || "left",
                        padding: "8px",
                        wordBreak: "break-word",
                        width: column.width || "auto",
                        borderBottom: (colIndex === 0 && hasFilter) ? "unset" : "",
                        backgroundColor: (colIndex === 0 && hasFilter) ? "#F3F3F3" : "",
                      }}
                    >
                      {Object.keys(row).length !== 0 && column.renderCell
                        ? column.renderCell({
                            value: row[column.field],
                            id: index,
                          })
                        : row[column.field]}
                    </td>
                  ))}
                  <td>
                    {Object.keys(row).length !== 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "end",
                          mr: "10px",
                          position: "static",
                        }}
                      >
                        {account?.roles?.[0]?.name === "Admin" && (
                          <>
                            {row.status.data === "Cerrado" ? (
                              <Tooltip title="Reabrir ticket" arrow placement="top">
                                <img src="../../assets/claims/table/re_open.svg" alt="reabrir-ticket" onClick={() => handleReOpenTicket(row)} style={{ cursor: "pointer", marginRight: "40px" }} />
                              </Tooltip>
                            ) : (
                              row.assigned === null ? (
                                <Tooltip title="Asignar ticket" arrow placement="top">
                                  <img src="../../assets/claims/table/paper_plane.svg" alt="asignar-ticket" onClick={() => handleAssignTicket(row)} style={{ cursor: "pointer", marginRight: "40px" }} />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Reasignar ticket" arrow placement="top">
                                  <img src="../../assets/claims/table/forward_circle.svg" alt="reasignar-ticket" onClick={() => handleReAssignTicket(row)} style={{ cursor: "pointer", marginRight: "40px" }} />
                                </Tooltip>
                              )
                            )}
                          </>
                        )}

                        <Tooltip title="Editar ticket" arrow placement="top">
                          <IconButton
                            onClick={() => handleEdit(row.uuid)}
                            aria-label="expand row"
                            size="small"
                            sx={{ ml: "10px" }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
      {totalPages > 1 && (
        <Box
          className="data-table-pagination"
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: "10px",
            justifyContent: "right",
          }}
        >
          {currentPage !== 1 && (
            <IconButton
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <KeyboardArrowLeftIcon
                sx={{ color: "#000", cursor: "pointer" }}
              />
            </IconButton>
          )}
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              className={`btn ${
                currentPage === index + 1 ? "btn-primary" : "btn-secondary"
              }`}
              onClick={() => handlePageChange(index + 1)}
              style={{ margin: "0 5px", cursor: "pointer" }}
            >
              {index + 1}
            </button>
          ))}
          {currentPage !== totalPages && (
            <IconButton
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <KeyboardArrowRightIcon
                sx={{ color: "#000", cursor: "pointer" }}
              />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DataGrid;